// Library methods
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LiveStreamImage from "./LiveStreamImage/LiveStreamImage";
import Loader from "src/components/shared/loaders/Loader/Loader";
import blackout from "src/assets/images/live-update/blackLive.jpeg";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Utils
import {
  byteToInt16,
  byteToInt32,
  combineInt8Array,
} from "src/utils/byte/byteHelper";

// Services
import { getIceEndpoint, getIceServers } from "src/services/webRtc/api";
import {
  invokeNotifyAnswerAccepted,
  invokeSendIceCandidate,
  invokeSendOffer,
} from "src/services/signalR/api";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type WebRtcType = {
  connection: HubConnection | null;
};

const parseSignalRData = (response: string) =>
  JSON.parse(JSON.parse(response).data);

const WebRtc = ({ connection }: WebRtcType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const { device, peerConnection, dataChannel, imageSrc, actions } =
    useLiveUpdateContext();
  const { setDataChannel, setImageSrc, setPeerConnection, stopWebRTC } =
    actions;

  const readyToGetFrame = useRef(true);
  const receivedLength = useRef(0);
  const dataByte = useRef(new Uint8Array(0));
  const dataId = useRef(0);
  const lastImageUrl = useRef<string | null>(null);
  const [currentConnectionState, setCurrentConnectionState] =
    useState<RTCIceConnectionState | null>(null);
  const [iceServersError, setIceServersError] = useState(false);

  const deviceId = device?.id;

  const deviceStage = useMemo(
    () => device?.deviceStatus?.stage,
    [device?.deviceStatus?.stage]
  );

  const hmdInProximity = Boolean(device?.deviceStatus?.hmdInProximity);

  const processImageData = useCallback(
    (byte: Uint8Array) => {
      readyToGetFrame.current = false;

      const url = URL.createObjectURL(new Blob([byte], { type: "image/jpeg" }));
      if (lastImageUrl.current) URL.revokeObjectURL(lastImageUrl.current);
      lastImageUrl.current = url;

      setImageSrc(url);

      readyToGetFrame.current = true;
    },
    [setImageSrc]
  );

  const handleReceiveData = useCallback(
    (event: MessageEvent) => {
      const receivedBytes = new Uint8Array(event.data);

      if (receivedBytes.length > 18) {
        const label = byteToInt16(receivedBytes, 0);

        if (label === 1001) {
          const dataIdReceived = byteToInt16(receivedBytes, 2);
          if (dataIdReceived !== dataId.current) receivedLength.current = 0;
          dataId.current = dataIdReceived;

          const dataLength = byteToInt32(receivedBytes, 4);
          const offset = byteToInt32(receivedBytes, 8);

          const isDesktopFrame = receivedBytes[14] !== 0;
          const metaByteLength = isDesktopFrame ? 24 : 15;

          if (receivedLength.current === 0)
            dataByte.current = new Uint8Array(dataLength);
          const chunkLength = receivedBytes.length - metaByteLength;

          if (offset + chunkLength <= dataByte.current.length) {
            receivedLength.current = receivedLength.current + chunkLength;
            combineInt8Array(
              dataByte.current,
              receivedBytes.slice(metaByteLength),
              offset
            );
          }

          if (readyToGetFrame.current && receivedLength.current === dataLength)
            processImageData(dataByte.current);
        }
      }
    },
    [processImageData]
  );

  const sendIceCandidate = useCallback(
    async (event: RTCPeerConnectionIceEvent) => {
      if (!deviceId || !connection) return;

      try {
        if (event.candidate) {
          const token = await getToken();
          const iceCandidate = {
            Candidate: event.candidate.candidate,
            SdpMid: event.candidate.sdpMid,
            SdpMlineIndex: event.candidate.sdpMLineIndex,
          };
          await invokeSendIceCandidate(
            token,
            deviceId,
            iceCandidate,
            connection
          );
        }
      } catch {
        // connection may have dropped before the ICE candidate could be sent
      }
    },
    [connection, deviceId, getToken]
  );

  const handleIceconnectionStateChange = useCallback(
    (event: Event) => {
      const currentIceConnectionState = (
        event?.currentTarget as RTCPeerConnection
      )?.iceConnectionState;

      if (currentIceConnectionState === "disconnected") {
        stopWebRTC();
      }
      setCurrentConnectionState(currentIceConnectionState);
    },
    [stopWebRTC]
  );

  const createWebRtcResources = useCallback(
    (iceServers: RTCIceServer[]) => {
      // reset refs so every new connection starts the frames from a clean slate
      readyToGetFrame.current = true;
      receivedLength.current = 0;
      dataByte.current = new Uint8Array(0);
      dataId.current = 0;

      const peerConnection = new RTCPeerConnection({ iceServers });
      peerConnection.onicecandidate = sendIceCandidate;
      peerConnection.oniceconnectionstatechange =
        handleIceconnectionStateChange;
      setPeerConnection(peerConnection);

      const dataChannel = peerConnection.createDataChannel("VRStreaming", {
        ordered: true,
      });
      dataChannel.onmessage = handleReceiveData;
      setDataChannel(dataChannel);
    },
    [
      handleIceconnectionStateChange,
      handleReceiveData,
      sendIceCandidate,
      setDataChannel,
      setPeerConnection,
    ]
  );

  const sendOffer = useCallback(async () => {
    if (!deviceId || !connection) return;

    try {
      const token = await getToken();
      const offer = await peerConnection?.createOffer();
      await peerConnection?.setLocalDescription(offer);
      await invokeSendOffer(token, deviceId, offer, connection);
    } catch (e) {
      console.error(e);
    }
  }, [connection, deviceId, getToken, peerConnection]);

  const handleWebRtcEvents = useCallback(() => {
    if (!deviceId || !connection) return;

    connection?.off("WebRtcAnswerReceived");
    connection?.on("WebRtcAnswerReceived", async (response) => {
      const answer = parseSignalRData(response);
      answer.type = "answer";
      await peerConnection?.setRemoteDescription(
        new RTCSessionDescription(answer)
      );
      const token = await getToken();
      try {
        await invokeNotifyAnswerAccepted(token, deviceId, connection);
      } catch {
        // connection may have dropped before the notification could be sent
      }
    });

    connection?.off("WebRtcIceCandidateReceived");
    connection?.on("WebRtcIceCandidateReceived", async (response) => {
      const iceCandidate = parseSignalRData(response);
      if (!iceCandidate) return;
      const candidate = new RTCIceCandidate({
        candidate: iceCandidate.Candidate,
        sdpMLineIndex: iceCandidate.SdpMLineIndex,
        sdpMid: iceCandidate.SdpMid,
      });
      try {
        await peerConnection?.addIceCandidate(candidate);
      } catch {
        // connection may have closed before the ICE candidate could be applied
      }
    });
  }, [connection, deviceId, getToken, peerConnection]);

  const getIceServersInfo = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await getIceEndpoint(token);

      if (!res?.iceEndpoint || !res?.credentials)
        throw new Error("Fatal: Receiving ICE endpoint");

      const iceServers = await getIceServers(res.iceEndpoint, res.credentials);
      setIceServersError(false);
      return iceServers;
    } catch (e) {
      console.error(e);
      setIceServersError(true);
    }
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;

    // Null out handlers before closing so the close event can't clobber the
    // new connection that createWebRtcResources is about to write into context.
    if (peerConnection) {
      peerConnection.oniceconnectionstatechange = null;
      peerConnection.onicecandidate = null;
      peerConnection.close();
    }
    if (dataChannel) {
      dataChannel.onmessage = null;
      dataChannel.close();
    }

    getIceServersInfo().then((iceServers: RTCIceServer[] | undefined) => {
      if (!cancelled && iceServers) createWebRtcResources(iceServers);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (lastImageUrl.current) URL.revokeObjectURL(lastImageUrl.current);
    };
  }, []);

  useEffect(() => {
    if (peerConnection) {
      handleWebRtcEvents();
      sendOffer();
    }
  }, [peerConnection, handleWebRtcEvents, sendOffer]);

  useEffect(() => {
    if (currentConnectionState !== "disconnected" || !deviceStage) return;

    let cancelled = false;
    setCurrentConnectionState(null);

    getIceServersInfo().then((iceServers: RTCIceServer[] | undefined) => {
      if (!cancelled && iceServers) createWebRtcResources(iceServers);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createWebRtcResources, deviceStage, getIceServersInfo]);

  if (iceServersError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography
          sx={{ fontSize: "14px", color: onSurface, textAlign: "center" }}
        >
          {t(
            "Could not connect to the live video stream. Please try again shortly."
          )}
        </Typography>
      </Box>
    );
  }

  if (!peerConnection || currentConnectionState === "disconnected") {
    return <Loader containerProps={{ height: "100%" }} />;
  }

  return (
    <LiveStreamImage
      src={imageSrc ?? blackout}
      deviceStageType={deviceStage?.stageType ?? ""}
      hmdInProximity={hmdInProximity}
      connection={connection}
    />
  );
};

export default WebRtc;
